import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import GlobalAuthAlertBanner, { useAuthAlerts, useNotificationCount } from "@/components/GlobalAuthAlertBanner";
import FeedbackWidget from "@/components/FeedbackWidget";
import OnboardingWizard, { useOnboarding } from "@/components/OnboardingWizard";
import {
  Activity,
  Users,
  ShieldCheck,
  BarChart3,
  CalendarClock,
  CalendarDays,
  FileBarChart,
  Archive,
  Stethoscope,
  UserCog,
  Upload,
  History,
  LogOut,
  UserCircle,
  Trash2,
  DatabaseBackup,
  Menu,
  KeyRound,
  Search,
  MessageSquare,
  ListOrdered,
  HelpCircle,
  Bell,
  ClipboardList,
  SquareCheckBig,
  ClipboardCheck,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useIdleTimeout } from "@/hooks/useIdleTimeout";
import IdleWarningModal from "@/components/IdleWarningModal";
import ThemeToggle from "@/components/ThemeToggle";
import CommandPalette from "@/components/CommandPalette";
import { queryClient } from "@/lib/queryClient";
import { toast } from "sonner";

const NAV = [
  { to: "/", label: "Dashboard", icon: Activity, end: true },
  { to: "/clinic-day-sheet", label: "Today's Clinic", icon: ClipboardCheck },
  { to: "/tasks", label: "My Tasks", icon: ClipboardList, badgeKey: "myTasks" },
  { to: "/todos", label: "To Do", icon: SquareCheckBig },
  { to: "/patients", label: "Patients", icon: Users },
  { to: "/program", label: "Program", icon: CalendarDays },
  { to: "/reports", label: "Reports", icon: FileBarChart },
  { to: "/authorisations", label: "Auth Alerts", icon: ShieldCheck, badgeKey: "authAlerts" },
  { to: "/transplant-load", label: "Transplant Load", icon: BarChart3 },
  { to: "/backlog", label: "Backlog", icon: Archive },
  { to: "/followups", label: "Follow-ups", icon: Stethoscope },
  { to: "/feedback", label: "Feedback", icon: MessageSquare },
  { to: "/help", label: "Help", icon: HelpCircle },
];

const ADMIN_NAV = [
  { to: "/admin/users", label: "Users", icon: UserCog },
  { to: "/admin/doctors", label: "Doctors", icon: Stethoscope },
  { to: "/admin/lists", label: "Lists", icon: ListOrdered },
  { to: "/admin/task-templates", label: "Task Templates", icon: ClipboardList },
  { to: "/admin/import", label: "Import XLSX", icon: Upload },
  { to: "/admin/audit", label: "Audit Log", icon: History },
  { to: "/admin/recycle-bin", label: "Recycle Bin", icon: Trash2 },
  { to: "/admin/backups", label: "Backups", icon: DatabaseBackup },
  { to: "/admin/diagnosis-review", label: "Diagnosis Review", icon: ShieldCheck },
  { to: "/admin/legacy-archives", label: "Legacy Archives", icon: Archive },
];

function roleBadgeColor(role) {
  if (role === "admin") return "bg-brand-100 text-brand-dark border-brand-200";
  if (role === "coordinator") return "bg-blue-100 text-blue-700 border-blue-200";
  return "bg-zinc-100 text-zinc-700 border-zinc-200";
}

const navTodayYMD = new Date().toISOString().slice(0, 10);

function NavList({ user, onItemClick, onReopenOnboarding }) {
  const { data: alertData } = useAuthAlerts();
  const authAlertsCount = alertData?.total || 0;

  const tasksNavQ = useQuery({
    queryKey: ["tasks", "mine", "nav"],
    queryFn: () =>
      api.get("/tasks/mine", { params: { status: "open,in_progress" } }).then((r) => r.data),
    staleTime: 60_000,
    enabled: !!user,
  });
  const tasksNavItems = tasksNavQ.data || [];
  const overdueCount = tasksNavItems.filter(
    (t) => t.due_date && t.due_date < navTodayYMD
  ).length;

  const renderBadge = (key) => {
    if (key === "authAlerts" && authAlertsCount > 0) {
      return (
        <span
          className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-100 text-rose-700 text-[11px] font-semibold tabular-nums"
          data-testid="nav-auth-alert-badge"
        >
          {authAlertsCount}
        </span>
      );
    }
    if (key === "myTasks" && overdueCount > 0) {
      return (
        <span
          className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-100 text-rose-700 text-[11px] font-semibold tabular-nums"
          data-testid="nav-tasks-overdue-badge"
        >
          {overdueCount}
        </span>
      );
    }
    return null;
  };
  return (
    <nav className="px-3 py-4 flex-1 overflow-y-auto flex flex-col">
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-zinc-500 dark:text-zinc-400 px-2 mb-2">
          Program
        </div>
        <ul className="space-y-0.5">
          {NAV.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                onClick={onItemClick}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 px-3 py-2.5 min-h-11 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-brand-50 dark:bg-brand-900/20 text-brand-dark dark:text-brand-200 before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-r-full before:bg-brand"
                      : "text-zinc-600 dark:text-zinc-400 hover:bg-brand-50/50 dark:hover:bg-zinc-800 hover:text-brand-dark dark:hover:text-zinc-100"
                  }`
                }
              >
                <item.icon className="w-[18px] h-[18px]" strokeWidth={1.6} />
                {item.label}
                {renderBadge(item.badgeKey)}
              </NavLink>
            </li>
          ))}
        </ul>
        {(user?.role === "admin" || user?.role === "coordinator") && (
          <>
            <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-zinc-400 dark:text-zinc-500 px-2 mt-6 mb-2">
              Administration
            </div>
            <ul className="space-y-0.5">
              {ADMIN_NAV.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    onClick={onItemClick}
                    data-testid={`nav-admin-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 min-h-11 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-brand-50 dark:bg-brand-900/20 text-brand-dark dark:text-brand-200"
                          : "text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:hover:text-zinc-100"
                      }`
                    }
                  >
                    <item.icon className="w-[18px] h-[18px]" strokeWidth={1.6} />
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* Getting Started — re-opens the onboarding wizard */}
      {onReopenOnboarding && (
        <div className="pt-3 mt-3 border-t border-zinc-100 dark:border-zinc-800">
          <button
            type="button"
            onClick={() => { onReopenOnboarding(); onItemClick?.(); }}
            data-testid="nav-getting-started"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors"
          >
            <HelpCircle className="w-[18px] h-[18px]" strokeWidth={1.6} />
            Getting Started
          </button>
        </div>
      )}
    </nav>
  );
}

function BrandHeader() {
  return (
    <div className="h-20 border-b border-zinc-200 dark:border-zinc-800 flex items-center gap-3 px-4 relative overflow-hidden shrink-0">
      <div className="absolute inset-0 brand-gradient opacity-[0.05] pointer-events-none" />
      <img
        src="/brand/logo.png"
        alt="Constantiaberg Haematology"
        className="h-8 w-auto relative"
        data-testid="sidebar-logo"
      />
      <div className="leading-tight relative">
        <div className="font-heading text-[14px] font-semibold tracking-tight text-brand-dark">
          Constantiaberg
        </div>
        <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-medium">
          Transplant Ops Hub
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const { count: alertCount, red: redCount } = useNotificationCount();
  const { open: onboardOpen, dismiss: dismissOnboard, reopen: reopenOnboard } = useOnboarding(user);

  // Close drawer on route change.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const onLogout = (reason) => {
    queryClient.clear();
    logout();
    navigate(reason === "idle" ? "/login?reason=idle" : "/login");
  };

  const idleMinutes = Number(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES || 15);
  const { warning, secondsLeft, dismiss } = useIdleTimeout({
    timeoutMinutes: idleMinutes,
    warnBeforeMinutes: 2,
    onTimeout: () => {
      toast.error("Signed out due to inactivity.");
      onLogout("idle");
    },
    enabled: !!user,
  });

  return (
    <div className="min-h-screen flex bg-cream dark:bg-background font-body text-zinc-800 dark:text-zinc-100">
      {/* Desktop sidebar (≥lg) */}
      <aside
        className="hidden lg:flex w-64 shrink-0 border-r border-zinc-200 dark:border-zinc-800 bg-white dark:bg-card flex-col"
        data-testid="desktop-sidebar"
      >
        <BrandHeader />
        <NavList user={user} onReopenOnboarding={reopenOnboard} />
        <div className="p-4 border-t border-zinc-200 dark:border-zinc-800 text-[11px] text-zinc-400 dark:text-zinc-600">
          Constantiaberg Haematology · v1.0
        </div>
      </aside>

      {/* Mobile drawer (<lg) */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent
          side="left"
          className="w-[80vw] max-w-[320px] p-0 flex flex-col bg-white dark:bg-card"
          data-testid="mobile-nav-drawer"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Main navigation</SheetTitle>
          </SheetHeader>
          <BrandHeader />
          <NavList user={user} onItemClick={() => setDrawerOpen(false)} onReopenOnboarding={reopenOnboard} />
          <div className="p-4 border-t border-zinc-200 dark:border-zinc-800 text-[11px] text-zinc-400 dark:text-zinc-600">
            Constantiaberg Haematology · v1.0
          </div>
        </SheetContent>
      </Sheet>

      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />

      <div className="flex-1 flex flex-col min-w-0">
        <header
          className="h-16 sticky top-0 z-40 bg-white/85 dark:bg-card/85 backdrop-blur-md border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between gap-2 px-3 sm:px-6 lg:px-8"
          data-testid="topbar"
        >
          {/* Left: hamburger (<lg) + brand wordmark */}
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden grid place-items-center w-11 h-11 -ml-1 rounded-md text-zinc-600 hover:bg-zinc-100 active:bg-zinc-200"
              aria-label="Open navigation"
              data-testid="topbar-hamburger"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="hidden lg:flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
              <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-medium">
                Live Ops Console
              </span>
            </div>
            {/* Mobile-only compact wordmark */}
            <Link to="/" className="lg:hidden flex items-center gap-2 min-w-0">
              <img
                src="/brand/logo.png"
                alt="Constantiaberg"
                className="h-7 w-auto shrink-0"
              />
              <span className="font-heading text-sm font-semibold text-brand-dark truncate">
                Transplant Ops Hub
              </span>
            </Link>
          </div>

          {/* Right: command palette trigger + theme toggle + user */}
          <div className="flex items-center gap-2" data-testid="topbar-user">
            {/* Command palette trigger */}
            <button
              type="button"
              onClick={() => setCmdOpen(true)}
              className="hidden sm:flex items-center gap-2 h-9 px-3 rounded-md border border-zinc-200 dark:border-zinc-700 text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300 hover:border-zinc-300 dark:hover:border-zinc-600 bg-white dark:bg-zinc-800 text-xs transition-colors"
              aria-label="Open command palette"
              data-testid="topbar-cmd-trigger"
            >
              <Search className="w-3.5 h-3.5" />
              <span>Search…</span>
              <kbd className="ml-1 inline-flex items-center gap-0.5 rounded border border-zinc-200 dark:border-zinc-600 px-1.5 py-0.5 text-[10px] font-medium text-zinc-400 dark:text-zinc-500">
                ⌘K
              </kbd>
            </button>

            {/* Desktop: name + role + logout inline */}
            <div className="hidden md:flex items-center gap-3">
              <div className="text-right leading-tight">
                <div
                  className="text-sm font-medium text-zinc-800 max-w-[16ch] truncate"
                  data-testid="topbar-email"
                >
                  {user?.name}
                </div>
                <div className="text-[11px] text-zinc-500 max-w-[20ch] truncate">
                  {user?.email}
                </div>
              </div>
              <Badge
                className={`capitalize ${roleBadgeColor(user?.role)}`}
                data-testid="topbar-role"
              >
                {user?.role}
              </Badge>
            </div>

            {/* Auth alerts bell */}
            <button
              type="button"
              onClick={() => setAlertsOpen(true)}
              className="relative p-2 rounded-md text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 dark:hover:text-zinc-300 dark:hover:bg-zinc-800 transition-colors"
              aria-label="Auth alerts"
              data-testid="topbar-alerts-trigger"
            >
              <Bell className="w-5 h-5" />
              {alertCount > 0 && (
                <span className={`absolute top-1 right-1 w-4 h-4 rounded-full ${redCount > 0 ? "bg-rose-500" : "bg-amber-400"} text-white text-[9px] font-bold flex items-center justify-center leading-none`}>
                  {alertCount > 9 ? "9+" : alertCount}
                </span>
              )}
            </button>

            <ThemeToggle />

            {/* Profile dropdown — works on every viewport, primary action on mobile */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="grid place-items-center w-11 h-11 rounded-md text-zinc-500 hover:text-brand hover:bg-brand-50"
                  aria-label="Profile menu"
                  data-testid="topbar-profile-trigger"
                >
                  <UserCircle className="w-6 h-6" strokeWidth={1.5} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="text-sm font-medium text-zinc-900 truncate">{user?.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
                  <Badge
                    className={`mt-1 capitalize ${roleBadgeColor(user?.role)}`}
                  >
                    {user?.role}
                  </Badge>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link
                    to="/profile"
                    className="cursor-pointer min-h-11 flex items-center"
                    data-testid="topbar-profile-link"
                  >
                    <UserCircle className="w-4 h-4 mr-2" /> Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link
                    to="/profile/change-password"
                    className="cursor-pointer min-h-11 flex items-center"
                    data-testid="topbar-change-password-link"
                  >
                    <KeyRound className="w-4 h-4 mr-2" /> Change password
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onLogout()}
                  className="cursor-pointer min-h-11 text-rose-600 focus:text-rose-700 focus:bg-rose-50"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <GlobalAuthAlertBanner open={alertsOpen} onClose={() => setAlertsOpen(false)} />
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
      <IdleWarningModal
        open={warning}
        secondsLeft={secondsLeft}
        onStay={dismiss}
        onLogoutNow={() => onLogout("idle")}
      />
      <FeedbackWidget />
      <OnboardingWizard user={user} open={onboardOpen} onClose={dismissOnboard} />
    </div>
  );
}
